import { DataGrid, EditorUtils } from "@serenity-is/corelib";
import { clearAllQuickFilters, findEditor } from "./ListPageChrome";

/**
 * v1's filter row, from the interactive prototype: a FILTERS eyebrow followed by one pill
 * per filter — "Type **All** ⌄" — each opening a popover, and a "Clear all" once anything
 * is set.
 *
 * ⚠ THIS IS v1 ONLY, deliberately. v2 keeps the Filters toggle and the collapsible panel
 * its own handoff draws. The two generations genuinely differ here, the same way their
 * grids differ in row actions and column count (see CLAUDE.md, "The grid's actions are NOT
 * the same in both generations") — one shared treatment would misrepresent both.
 *
 * WHY THE POPOVER HOSTS THE REAL EDITOR
 * The prototype draws each popover as a list of checkboxes. Rebuilding that means
 * enumerating every editor type's option list — enums from their registered keys, lookups
 * from their async lookup, strings from nothing at all — and re-implementing selection on
 * top. That duplicates state Serenity already owns and drifts the moment a filter's editor
 * type changes. So the pill MOVES the existing `.quick-filter-item` into its popover, label
 * and all: the editor keeps its own handlers, its own data source and its own multi-select
 * behaviour, and the pill only reflects what the editor says. Same principle as the rest of
 * ListPageChrome — move the real control into the designed slot rather than reimplement it.
 * The visible difference from the doc is that a popover shows the editor rather than a
 * checkbox list.
 */

const OPEN = "is-open";

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string) {
    const node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text;
    return node;
}

interface Pill {
    root: HTMLElement;
    value: HTMLElement;
    pop: HTMLElement;
    item: HTMLElement;
}

export function installFilterPills(card: HTMLElement, tools: HTMLElement, filtersBar: HTMLElement, grid: DataGrid<any>) {
    const bar = el("div", "m-filterbar");
    bar.appendChild(el("span", "m-filterbar-label", "Filters"));

    const pills: Pill[] = [];
    const items = Array.from(filtersBar.querySelectorAll<HTMLElement>(".quick-filter-item"));

    for (const item of items) {
        const label = (item.querySelector(".quick-filter-label")?.textContent || "").replace(/[:：]\s*$/, "").trim();

        const pill = el("button", "m-fpill") as HTMLButtonElement;
        pill.type = "button";
        pill.setAttribute("aria-expanded", "false");
        pill.append(
            el("span", "m-fpill-key", label),
            el("span", "m-fpill-val", "All"),
            el("i", "m-fpill-caret fa fa-angle-down"));

        const pop = el("div", "m-fpill-pop");
        pop.hidden = true;
        // The editor comes across whole, in its own wrapper, so nothing it holds a
        // reference to changes — only where it sits in the document.
        pop.appendChild(item);

        const wrap = el("div", "m-fpill-wrap");
        wrap.append(pill, pop);
        bar.appendChild(wrap);

        pill.addEventListener("click", () => {
            const open = pop.hidden;
            closeAll();
            if (open) {
                pop.hidden = false;
                pill.classList.add(OPEN);
                pill.setAttribute("aria-expanded", "true");
                // Focus goes to the editor rather than staying on the pill, so a keyboard
                // user lands in the control they just opened.
                pop.querySelector<HTMLElement>("input, select, textarea")?.focus?.();
            }
        });

        pills.push({ root: pill, value: pill.querySelector(".m-fpill-val")!, pop, item });
    }

    const clear = el("button", "m-fpill-clear") as HTMLButtonElement;
    clear.type = "button";
    clear.append(el("i", "fa fa-times"), el("span", undefined, "Clear all"));
    clear.hidden = true;
    clear.addEventListener("click", () => {
        clearAllQuickFilters(card);
        setTimeout(sync, 0);
    });
    bar.appendChild(clear);

    // The row sits under the tools row and above the table, which is where the prototype
    // puts it — filters belong with the data, not with the page actions.
    tools.insertAdjacentElement("afterend", bar);

    function closeAll() {
        for (const p of pills) {
            p.pop.hidden = true;
            p.root.classList.remove(OPEN);
            p.root.setAttribute("aria-expanded", "false");
        }
    }

    /** Pill labels and the Clear all visibility, derived from the editors themselves. */
    function sync() {
        let active = 0;
        for (const p of pills) {
            const found = findEditor(p.item);
            let text = "";
            if (found) {
                try {
                    const value = EditorUtils.getValue(found.widget);
                    const empty = value == null || value === "" ||
                        (Array.isArray(value) && value.length === 0);
                    if (!empty) text = EditorUtils.getDisplayText(found.widget) || "";
                } catch { /* an editor that will not answer is treated as unset */ }
            }
            p.root.classList.toggle("is-set", !!text);
            p.value.textContent = text || "All";
            if (text) active++;
        }
        clear.hidden = active === 0;
    }

    // A change on any editor bubbles to the bar, so no per-editor subscription is needed.
    bar.addEventListener("change", () => setTimeout(sync, 0), true);
    grid.onDataChanged?.subscribe(() => sync());
    sync();

    // ⚠ Capture phase, and it must ignore clicks INSIDE a popover. select2 renders its
    // dropdown into a body-level container, so a click on an option is outside the popover
    // in DOM terms; closing on it would shut the pill the moment a value was picked.
    document.addEventListener("mousedown", e => {
        const t = e.target as HTMLElement;
        if (t?.closest?.(".m-fpill-wrap") || t?.closest?.(".select2-drop") || t?.closest?.(".flatpickr-calendar"))
            return;
        closeAll();
    }, true);

    document.addEventListener("keydown", e => {
        if (e.key === "Escape" && pills.some(p => !p.pop.hidden)) closeAll();
    });

    return bar;
}
